import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { SERVICEURL } from "../../appconfig";
import { sortWorkExperiencesByDate } from "../../shared/utils/recordDateSort";

interface EducationInfo {
  collageParameterName: string;
  degree: string;
  departmentParameterName: string;
  educationID: string;
  graduationStatusName: string;
  graduationYear: string;
  isCertificate: string;
  isItPersonnel: string;
  memberID: string;
  registeredYear: string | null;
}

interface WorkExperience {
  companyName: string;
  consultantComments: string | null;
  endDate: string | null;
  positionName: string;
  reasonForLeave: string | null;
  startDate: string;
  statement: string;
  usedTechnologies: string | null;
}

interface StateInfo {
  stateInfoID: string;
  memberID: string;
  statusName: string;
  subStatusName: string;
  interviewMemberName: string | null;
  resultParameterName: string;
  interviewDate: string;
  createDate: string;
  customerName: string;
  customerId: string | null;
  positionName: string;
  projectName: string;
  subStatusID: string;
  relevantExpert: string | null;
  locationName: string | null;
  locationAddressName: string | null;
  statusNotes: string;
  salaryExpectationID: string | null;
  tcNo: string;
  interviewAiId: string | null;
  kvkkApproved: boolean | null;
  isKvkkSent: boolean | null;
  isInsource?: boolean | null;
  manuelScore?: number | null;
}

interface LanguageInfo {
  languageID: string;
  memberID: string;
  languageName: string;
  speakingLevel: string;
  writingLevel: string;
  readingLevel: string;
  isEnough: string | null;
}

interface SalaryExpectation {
  id: string;
  memberId: string;
  salaryExpectation: number;
  createdDate: string;
}

interface TechnicSkill {
  isTechnicId: 1 | 2 | 3;
  levelName: string;
  memberID: string;
  technicID: string;
  technicName: string;
  year: number | null;
}

interface PhoneInfo {
  phoneID: string;
  memberID: string;
  communicationType: string;
  phoneNo: string;
}

interface EmailInfo {
  emailID: string;
  memberID: string;
  communicationType: string;
  emailName: string;
}

interface AddressInfo {
  addressID: string;
  memberID: string;
  communicationType: string;
  countryName: string;
  cityName: string;
  townName: string;
  addressLine: string;
  postcode: string;
  fullAddress: string;
}

interface SalaryAndNoticeInfo {
  noticeTime: string;
  salaryExpectation: SalaryExpectation[];
}

interface ContactInfo {
  phones: PhoneInfo[];
  emails: EmailInfo[];
  addresses: AddressInfo[];
}

interface CandidateData {
  educationList: EducationInfo[];
  experienceList: WorkExperience[];
  skills: TechnicSkill[];
  status: StateInfo[];
  languages: LanguageInfo[];
  salaryExpectations: SalaryExpectation[];
  contact: ContactInfo;
}

export type CandidateDataSection =
  | "education"
  | "experience"
  | "languages"
  | "salary"
  | "phones"
  | "emails"
  | "addresses";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Backend bazi alanlari PascalCase donderebiliyor; ikisini de karsila.
// educationID / memberID Guid string — upstream burada `?? 0` kullaniyor.
const normalizeEducationList = (educationData: any[]): EducationInfo[] =>
  educationData.map((edu: any) => ({
    ...edu,
    registeredYear: edu.registeredYear ?? edu.RegisteredYear ?? null,
    graduationYear: edu.graduationYear ?? edu.GraduationYear ?? "",
    collageParameterName:
      edu.collageParameterName ?? edu.CollageParameterName ?? "",
    departmentParameterName:
      edu.departmentParameterName ?? edu.DepartmentParameterName ?? "",
    graduationStatusName:
      edu.graduationStatusName ?? edu.GraduationStatusName ?? "",
    degree: edu.degree ?? edu.Degree ?? "",
    isCertificate: edu.isCertificate ?? edu.IsCertificate ?? "",
    isItPersonnel: edu.isItPersonnel ?? edu.IsItPersonnel ?? "",
    educationID: String(edu.educationID ?? edu.EducationID ?? ""),
    memberID: String(edu.memberID ?? edu.MemberID ?? ""),
  }));

const fetchEducationList = async (
  memberId: string,
): Promise<EducationInfo[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetEducationInfos/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const educationData = response.data || [];
  return educationData.length > 0 ? normalizeEducationList(educationData) : [];
};

const fetchExperienceList = async (
  memberId: string,
): Promise<WorkExperience[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetWorkExperiences/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const experienceData = response.data || [];
  return experienceData.length > 0
    ? sortWorkExperiencesByDate(experienceData)
    : [];
};

const fetchLanguageList = async (
  memberId: string,
): Promise<LanguageInfo[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetLanguageInfos/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const languageData = response.data?.data || [];
  return languageData.length > 0 ? languageData : [];
};

const fetchPhoneList = async (memberId: string): Promise<PhoneInfo[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetPhoneInfos/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const phoneData = response.data?.data || [];
  return phoneData.length > 0 ? phoneData : [];
};

const fetchEmailList = async (memberId: string): Promise<EmailInfo[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetEmailInfos/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const emailData = response.data?.data || [];
  return emailData.length > 0 ? emailData : [];
};

const fetchAddressList = async (
  memberId: string,
): Promise<AddressInfo[]> => {
  const response = await axios.get(
    `${SERVICEURL}api/Candidates/GetAddressInfos/${memberId}`,
    { headers: getAuthHeaders() },
  );
  const addressData = response.data?.data || [];
  return addressData.length > 0 ? addressData : [];
};

const fetchStateInfo = async (memberId: string): Promise<StateInfo[]> => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.get<StateInfo[]>(
      `${SERVICEURL}api/Candidates/StateInfoListByMemberID/${memberId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const raw = response.data || [];
    return raw.map((item: any) => ({
      ...item,
      subStatusID: String(item.subStatusID ?? item.subStatusId ?? ""),
      isInsource: item.isInsource ?? item.IsInsource ?? false,
    }));
  } catch (error) {
    console.error("StateInfo fetch error:", error);
    // Error durumunda boş array döndür, throw etme
    return [];
  }
};

const fetchSalaryAndNoticeInfo = async (
  memberId: string,
): Promise<SalaryAndNoticeInfo> => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.get(
      `${SERVICEURL}api/Candidates/GetSalaryAndNoticeInfo/${memberId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data.data || { noticeTime: "", salaryExpectation: [] };
  } catch (error) {
    console.error("SalaryAndNoticeInfo fetch error:", error);
    return { noticeTime: "", salaryExpectation: [] };
  }
};

const fetchTechnicalSkillsInfo = async (
  memberId: string,
): Promise<TechnicSkill[]> => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.get(
      `${SERVICEURL}api/Candidates/GetTechnicSkillInfos/${memberId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data?.data || [];
  } catch (error) {
    console.error("TechnicalSkillsInfo fetch error:", error);
    // Error durumunda boş array döndür, throw etme
    return [];
  }
};

const fetchSalaryExpectations = async (
  memberId: string,
): Promise<SalaryExpectation[]> => {
  const result = await fetchSalaryAndNoticeInfo(memberId);
  const salaryData = result.salaryExpectation || [];
  return salaryData.length > 0 ? salaryData : [];
};

const emptyCandidateData = (): CandidateData => ({
  educationList: [],
  experienceList: [],
  skills: [],
  status: [],
  languages: [],
  salaryExpectations: [],
  contact: { phones: [], emails: [], addresses: [] },
});

const settledValue = <T,>(
  result: PromiseSettledResult<T>,
  label: string,
  fallback: T,
): T => {
  if (result.status === "fulfilled") return result.value;
  console.error(`${label} API error:`, result.reason);
  return fallback;
};

const fetchCandidateData = async (memberId: string): Promise<CandidateData> => {
  try {
    const [
      educationResult,
      experienceResult,
      stateInfoResult,
      languageResult,
      salaryResult,
      skillsResult,
      phoneResult,
      emailResult,
      addressResult,
    ] = await Promise.allSettled([
      fetchEducationList(memberId),
      fetchExperienceList(memberId),
      fetchStateInfo(memberId),
      fetchLanguageList(memberId),
      fetchSalaryExpectations(memberId),
      fetchTechnicalSkillsInfo(memberId),
      fetchPhoneList(memberId),
      fetchEmailList(memberId),
      fetchAddressList(memberId),
    ]);

    return {
      educationList: settledValue(educationResult, "Education", []),
      experienceList: settledValue(experienceResult, "Experience", []),
      // DIKKAT: upstream c3d23e5 burada `skillsRaw?.data` okuyor ama
      // fetchTechnicalSkillsInfo zaten DIZI donduruyor — dizide `.data` olmadigi
      // icin upstream'de skills HER ZAMAN bos kaliyor. Bu bir gerileme; port
      // edilmedi, degeri dogrudan okuyoruz (bizdeki onceki davranis da boyleydi).
      skills: settledValue(skillsResult, "Skills", []),
      status: settledValue(stateInfoResult, "StateInfo", []),
      languages: settledValue(languageResult, "Language", []),
      salaryExpectations: settledValue(salaryResult, "Salary", []),
      contact: {
        phones: settledValue(phoneResult, "Phone", []),
        emails: settledValue(emailResult, "Email", []),
        addresses: settledValue(addressResult, "Address", []),
      },
    };
  } catch (error) {
    console.error("Candidate data fetch error:", error);
    return emptyCandidateData();
  }
};

export const useCandidateData = (memberId: string) => {
  const [data, setData] = useState<CandidateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!memberId) return;

    try {
      setLoading(true);
      setError(null);
      const candidateData = await fetchCandidateData(memberId);
      setData(candidateData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  /**
   * Belirli bir bolumu loading GOSTERMEDEN yeniler.
   * Mutasyon sonrasi tum veriyi cekmek yerine yalnizca ilgili bolum cekilir;
   * loading tetiklenmedigi icin accordion durumu bozulmaz.
   */
  const refetchSection = useCallback(
    async (section: CandidateDataSection) => {
      if (!memberId) return;

      try {
        switch (section) {
          case "education": {
            const educationList = await fetchEducationList(memberId);
            setData((prev) => (prev ? { ...prev, educationList } : prev));
            break;
          }
          case "experience": {
            const experienceList = await fetchExperienceList(memberId);
            setData((prev) => (prev ? { ...prev, experienceList } : prev));
            break;
          }
          case "languages": {
            const languages = await fetchLanguageList(memberId);
            setData((prev) => (prev ? { ...prev, languages } : prev));
            break;
          }
          case "salary": {
            const salaryExpectations = await fetchSalaryExpectations(memberId);
            setData((prev) => (prev ? { ...prev, salaryExpectations } : prev));
            break;
          }
          case "phones": {
            const phones = await fetchPhoneList(memberId);
            setData((prev) =>
              prev ? { ...prev, contact: { ...prev.contact, phones } } : prev,
            );
            break;
          }
          case "emails": {
            const emails = await fetchEmailList(memberId);
            setData((prev) =>
              prev ? { ...prev, contact: { ...prev.contact, emails } } : prev,
            );
            break;
          }
          case "addresses": {
            const addresses = await fetchAddressList(memberId);
            setData((prev) =>
              prev ? { ...prev, contact: { ...prev.contact, addresses } } : prev,
            );
            break;
          }
        }
      } catch (err) {
        console.error(`Section refetch error (${section}):`, err);
      }
    },
    [memberId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData, refetchSection };
};

export const useAllStateInfos = (memberId: string) => {
  const [allStateInfos, setAllStateInfos] = useState<StateInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async () => {
    if (!memberId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchStateInfo(memberId);
      setAllStateInfos(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [memberId]);

  return { allStateInfos, loading, error, refetch };
};

export const useTechnicalSkillsInfo = (memberId: string) => {
  const [technicalSkills, setTechnicalSkills] = useState<TechnicSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async () => {
    if (!memberId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTechnicalSkillsInfo(memberId);
      setTechnicalSkills(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [memberId]);

  return { technicalSkills, loading, error, refetch };
};

// Yeni hook: technicParameterID ile filtrelenmiş yetkinlikler
const fetchTechnicalSkillsInfoByType = async (
  memberId: string,
  technicParameterID?: number,
): Promise<TechnicSkill[]> => {
  const token = localStorage.getItem("token");

  try {
    const url = `${SERVICEURL}api/Candidates/GetTechnicSkillInfos/${memberId}`;
    const response = await axios.get(url, {
      params: technicParameterID ? { technicParameterID } : {},
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data?.data || [];
  } catch (error) {
    console.error("TechnicalSkillsInfo by type fetch error:", error);
    return [];
  }
};

export const useTechnicalSkillsInfoByType = (
  memberId: string,
  technicParameterID?: number,
) => {
  const [technicalSkills, setTechnicalSkills] = useState<TechnicSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async () => {
    if (!memberId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTechnicalSkillsInfoByType(
        memberId,
        technicParameterID,
      );
      setTechnicalSkills(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [memberId, technicParameterID]);

  return { technicalSkills, loading, error, refetch };
};

// Spesifik yetkinlik tipleri için kısa hooklar
export const useTeknikYetkinlikler = (memberId: string) =>
  useTechnicalSkillsInfoByType(memberId, 1);

export const useKisiselYetkinlikler = (memberId: string) =>
  useTechnicalSkillsInfoByType(memberId, 2);

export const useDomainYetkinlikler = (memberId: string) =>
  useTechnicalSkillsInfoByType(memberId, 3);

// Export interfaces for use in other components
export type {
  StateInfo,
  EducationInfo,
  WorkExperience,
  LanguageInfo,
  SalaryExpectation,
  TechnicSkill,
  PhoneInfo,
  EmailInfo,
  AddressInfo,
  ContactInfo,
  CandidateData,
};
